import domtoimage from 'dom-to-image';

/**
 * Compresses a base64 image data URL to reduce file size
 *
 * @param dataUrl Base64 image data URL
 * @param maxSizeKB Maximum size in KB (default: 100KB)
 * @returns Promise that resolves with compressed data URL or null if compression fails
 */
const compressImage = async (
  dataUrl: string,
  maxSizeKB: number = 100
): Promise<string | null> => {
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve(null);
        return;
      }

      const maxDimension = 1200;
      let { width, height } = img;

      if (width > height) {
        if (width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        }
      } else {
        if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
      }

      canvas.width = width;
      canvas.height = height;

      ctx.drawImage(img, 0, 0, width, height);

      const maxSizeBytes = maxSizeKB * 1024;
      for (let quality = 0.8; quality >= 0.3; quality -= 0.1) {
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        if (compressedDataUrl.length < maxSizeBytes) {
          resolve(compressedDataUrl);
          return;
        }
      }

      const finalCompressed = canvas.toDataURL('image/jpeg', 0.3);
      resolve(finalCompressed);
    };

    img.onerror = () => {
      resolve(null);
    };

    img.src = dataUrl;
  });
};

/**
 * Captures an HTML element and returns a compressed Base64-encoded image string.
 * @param element The HTML element to capture.
 * @param maxSizeKB Maximum size in KB (default: 100KB)
 * @returns A promise that resolves with the compressed Base64 image string or null on error.
 */
export const captureScreenshot = async (
  element: HTMLElement,
  maxSizeKB: number = 100
): Promise<string | null> => {
  if (!element) {
    return null;
  }

  try {
    const dataUrl = await domtoimage.toPng(element);

    if (!dataUrl) {
      return null;
    }

    if (dataUrl.length < maxSizeKB * 1024) {
      return dataUrl;
    }

    const compressedDataUrl = await compressImage(dataUrl, maxSizeKB);
    return compressedDataUrl;
  } catch (error) {
    return null;
  }
};
